import { apiUrl, requestApi } from "./api";

export interface MemberView {
  memberCode: string;
  loginId: string | null;
  name: string;
  email: string;
  phone: string | null;
  authProvider: "EMAIL" | "KAKAO";
  birthDate: string | null;
  gender: "MALE" | "FEMALE" | "UNSPECIFIED" | null;
  heightCm: number | null;
  weightKg: number | null;
  activityLevel: string | null;
  healthGoal: string | null;
  diseases: string | null;
  allergyFoods: string | null;
  foodPreferences: string | null;
  eatsAlone: boolean | null;
  diningPartySize: "ONE" | "TWO" | "THREE_TO_FOUR" | "FIVE_PLUS" | null;
  profileImageKey: string | null;
  profileCompletedAt: string | null;
  subscriptionEndedAt: string | null;
  trialAvailable: boolean;
  subscriptionStatus: "PAID" | "FREE_TRIAL" | "NONE";
  subscriptionCancelledAt: string | null;
}

export interface SavedRecipeView {
  savedRecipeCode: string;
  recipeId: number;
  name: string;
  description: string;
  kcal: number;
  carbs: number;
  protein: number;
  fat: number;
  cookingMinutes: number;
  difficulty: "쉬움" | "보통" | "어려움";
  servings: number;
  ingredients: string[];
  steps: string[];
  savedAt: string;
}

export interface MemberPaymentView {
  paymentCode: string;
  planName: string;
  amountKrw: number;
  method: string | null;
  status: string;
  paidAt: string;
  subscriptionStartedAt: string | null;
  subscriptionEndedAt: string | null;
}

export interface SubscriptionPlanView {
  planCode: string;
  name: string;
  durationMonths: number;
  priceKrw: number;
}

export interface PaymentOrderView {
  clientKey: string;
  paymentCode: string;
  orderId: string;
  orderName: string;
  amount: number;
  customerKey: string;
  customerName: string;
  customerEmail: string;
  customerMobilePhone: string;
}

export interface MemberInquiryView {
  inquiryCode: string;
  category: "AI_RECOMMENDATION" | "RECIPE" | "SERVICE" | "OTHER";
  title: string;
  content: string;
  status: "WAITING" | "ANSWERED";
  answer: string | null;
  answeredAt: string | null;
  createdAt: string;
}

function memberToken() {
  try {
    const session = JSON.parse(sessionStorage.getItem("betterTableMemberSession") ?? "{}");
    if (typeof session.token === "string" && session.token) return session.token as string;
  } catch {
    // The API returns a consistent authentication error below.
  }
  throw new Error("로그인이 필요합니다.");
}

function memberRequest<T>(path: string, init?: RequestInit) {
  try {
    return requestApi<T>(path, {
      ...init,
      headers: { Authorization: `Bearer ${memberToken()}`, ...init?.headers },
    });
  } catch (error) {
    return Promise.reject(error instanceof Error ? error : new Error("로그인이 필요합니다."));
  }
}

export const getMyMember = () => memberRequest<MemberView>("/members/me");
export const updateMyMember = (body: { name: string; phone: string; password?: string }) =>
  memberRequest<MemberView>("/members/me", { method: "PATCH", body: JSON.stringify(body) });
export const updateMyProfile = (body: Partial<MemberView>) =>
  memberRequest<MemberView>("/members/me/profile", { method: "PATCH", body: JSON.stringify(body) });
export const uploadMyProfileImage = (image: File) => {
  const body = new FormData();
  body.append("image", image);
  return memberRequest<MemberView>("/members/me/profile-image", { method: "POST", body });
};
export async function getMyProfileImage() {
  const response = await fetch(apiUrl("/members/me/profile-image"), {
    headers: { Authorization: `Bearer ${memberToken()}` },
  });
  if (!response.ok) throw new Error("프로필 이미지를 불러오지 못했습니다.");
  return response.blob();
}
export const withdrawMyMember = () => memberRequest<void>("/members/me", { method: "DELETE" });

export const listSavedRecipes = () => memberRequest<SavedRecipeView[]>("/saved-recipes");
export const saveRecipe = (recipeId: number) => memberRequest<SavedRecipeView>(`/saved-recipes/${recipeId}`, { method: "POST" });
export const deleteSavedRecipe = (code: string) => memberRequest<void>(`/saved-recipes/${encodeURIComponent(code)}`, { method: "DELETE" });

export const listMemberPayments = () => memberRequest<MemberPaymentView[]>("/payments");
export const getMemberPayment = (code: string) => memberRequest<MemberPaymentView>(`/payments/${encodeURIComponent(code)}`);
export const listSubscriptionPlans = () => memberRequest<SubscriptionPlanView[]>("/payments/plans");
export const startSubscriptionTrial = () => memberRequest<{ startedAt: string; endedAt: string }>("/payments/trial", {
  method: "POST",
});
export const cancelMySubscription = () => memberRequest<{
  paymentCode: string;
  subscriptionCancelledAt: string;
  subscriptionEndedAt: string;
}>("/payments/subscription/cancel", { method: "POST" });
export const createPaymentOrder = (planCode: string) => memberRequest<PaymentOrderView>("/payments/orders", {
  method: "POST",
  body: JSON.stringify({ planCode }),
});
export const confirmPayment = (body: { paymentKey: string; orderId: string; amount: number }) =>
  memberRequest<MemberPaymentView>("/payments/confirm", { method: "POST", body: JSON.stringify(body) });
export const recordPaymentFailure = (body: { orderId: string; code?: string; message?: string }) =>
  memberRequest<void>("/payments/fail", { method: "POST", body: JSON.stringify(body) });

export const listMemberInquiries = () => memberRequest<MemberInquiryView[]>("/inquiries");
export const getMemberInquiry = (code: string) => memberRequest<MemberInquiryView>(`/inquiries/${encodeURIComponent(code)}`);
export const createMemberInquiry = (body: { category: MemberInquiryView["category"]; title: string; content: string }) =>
  memberRequest<MemberInquiryView>("/inquiries", { method: "POST", body: JSON.stringify(body) });
